import type { TableRecord } from '@powerportalspro/core';
import { PowerPortalsProProvider, RecordContext } from '@powerportalspro/react';
import {
  FluentDialogProvider,
  FluentOverlayProvider,
  LocalizationBoundary,
  officeColorValueToAccentName,
  PowerPortalsProThemeProvider,
  ThemeMode,
  useTheme,
} from '@powerportalspro/react-fluent';
import { MapsProvider } from '@powerportalspro/react-maps';
import { useEffect, type ComponentType, type ReactNode } from 'react';
import { createRoot, type Root } from 'react-dom/client';

// Fluent UI Blazor v5 keeps the visitor's theme (mode, brand color) in localStorage under this key.
const FLUENT_THEME_STORAGE_KEY = 'fluentui-blazor:theme-settings';

function readFluentThemeSettings(): { mode?: unknown; color?: unknown; base?: unknown } | null {
  try {
    const raw = window.localStorage.getItem(FLUENT_THEME_STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    // localStorage unavailable / invalid JSON.
    return null;
  }
}

/**
 * Reads the light/dark mode the Blazor host is showing. Fluent UI Blazor v5 marks dark mode with
 * `data-theme="dark"` on body and removes the attribute for light mode. Until its script has run,
 * the attribute is whatever the host server-rendered from the `ppp-theme` cookie: "dark", "light",
 * or nothing for a visitor who follows the system. So with no attribute, the visitor's stored
 * choice decides, and with no stored choice the system preference does, as it does for Fluent UI.
 */
function readBlazorThemeMode(): ThemeMode {
  const bodyTheme = document.body?.dataset.theme;
  if (bodyTheme === 'dark') return ThemeMode.Dark;
  if (bodyTheme === 'light') return ThemeMode.Light;
  const mode = readFluentThemeSettings()?.mode;
  if (mode === 'dark') return ThemeMode.Dark;
  return mode === 'light' ? ThemeMode.Light : ThemeMode.System;
}

/**
 * Reads the Blazor host's brand color and resolves it to one of the React provider's built-in
 * accent names (`'default'`, `'excel'`, `'word'`, ...) through `officeColorValueToAccentName`.
 * Fluent UI Blazor v5 takes the color from body's `data-theme-color` when the site sets one, and
 * otherwise from the visitor's stored settings, where a color applies only when its base is
 * "brand"; with neither it uses its default brand, which is the React provider's `'default'`.
 * Returns `null` for a color outside the Office palette, which leaves the sample's accent alone.
 */
function readBlazorAccentName(): string | null {
  const settings = readFluentThemeSettings();
  const color =
    document.body?.getAttribute('data-theme-color') ||
    (settings?.base === 'brand' ? settings.color : null);
  if (typeof color !== 'string' || color === '') return 'default';
  return officeColorValueToAccentName(color);
}

/**
 * Internal bridge component: pushes the Blazor host's theme mode AND
 * accent color into the surrounding `<PowerPortalsProThemeProvider>` on
 * mount and whenever the Blazor side changes either.
 *
 * Renders nothing — only the side-effects of `useTheme().setMode(...)`
 * and `setAccentColor(...)` matter. The provider's `storageKey` is
 * sample-scoped so these writes don't clobber the user's actual
 * React-side theme preference (consumer React apps write to
 * `ppp-theme-mode` / `ppp-theme-accent`; samples write to separate keys).
 */
function BlazorThemeSync(): null {
  const { setMode, setAccentColor } = useTheme();

  useEffect(() => {
    const syncAccent = () => {
      const accent = readBlazorAccentName();
      if (accent !== null) setAccentColor(accent);
    };

    setMode(readBlazorThemeMode());
    syncAccent();

    // Fluent UI Blazor v5 raises `themeChanged` on body each time it applies a theme, for a change
    // of mode or of color and from any source (the site settings, the system color scheme,
    // another tab), after saving the settings and with the resolved mode in `detail.isDark`.
    const onThemeChanged = (event: Event) => {
      const isDark = (event as CustomEvent<{ isDark?: boolean }>).detail?.isDark === true;
      setMode(isDark ? ThemeMode.Dark : ThemeMode.Light);
      syncAccent();
    };
    document.body.addEventListener('themeChanged', onThemeChanged);

    return () => document.body.removeEventListener('themeChanged', onThemeChanged);
  }, [setMode, setAccentColor]);

  return null;
}

/**
 * Optional wrappers the docs envelope mounts around a sample. Lifts
 * boilerplate (a `<RecordContext>` for editor samples, etc.) OUT of the
 * sample file so the source tab shows just the meaningful bit — mirrors
 * how Blazor demo `.razor` files contain only `<TextEdit ColumnName=".." />`
 * while the surrounding page (`TextEditDemoPage.razor`) supplies the
 * `<RecordContext>`. Each option toggles its wrapper on; sample files stay
 * focused on the component they're documenting.
 */
export interface SampleConfig {
  /** Wraps the sample in a `<RecordContext>` so column-bound editors have
   *  somewhere to bind. Omit when the sample manages its own RecordContext
   *  (or doesn't need one — e.g. layout-only samples). */
  recordContext?: {
    /** Table logical name (e.g. `"ppp_democustomtable"`). */
    table: string;
    /** Optional record id. Omit for create-mode (fresh record, no fetch). */
    id?: string;
    /** When `true`, skip the server fetch and seed RecordContext with a
     *  stub `{ tableName, id }` record via the `loadedRecord` prop. Mirrors
     *  the Blazor demo pattern of passing a hand-constructed `TableRecord`
     *  directly into `<RecordContext Record="...">` for cases where the
     *  descendants only need the parent reference (table + id), not the
     *  loaded column values — e.g. SubGrid samples that use the parent
     *  as a relationship filter key. Without this, a failing or slow
     *  fetch would leave the sample area blank because descendants gate
     *  on `record !== undefined`. Has no effect without `id`. */
    skipFetch?: boolean;
  };
}

/**
 * Registers a React component as a browser custom element so it can be
 * mounted from a non-React host page (the Blazor demo site) by emitting
 * `<tag-name></tag-name>` markup. The host loads `samples.js` once; after
 * that any number of these tags on the page just work.
 *
 * Each instance constructs its own provider envelope — FluentUI v9 theme,
 * PowerPortalsPro client/auth/localization, dialog + overlay portals,
 * plus any config-driven wrappers (`<RecordContext>`, ...) — so the sample
 * file stays minimal and the surrounding host page needs nothing
 * React-specific.
 *
 * Light-DOM mount (no `attachShadow`): FluentUI v9 injects styles into
 * `document.head` via Griffel, and a shadow root would isolate those
 * styles away from the rendered subtree. Light DOM is the path of least
 * resistance for FluentUI 9 + custom elements; downside is the parent
 * page's CSS can reach the sample, which is fine for these docs.
 */
export function defineSampleElement(
  tagName: string,
  Component: ComponentType,
  config?: SampleConfig,
): void {
  if (typeof customElements === 'undefined') return;
  if (customElements.get(tagName)) return;

  class SampleElement extends HTMLElement {
    private mounted: MountedSample | null = null;
    private pendingMount: number | null = null;

    connectedCallback(): void {
      // Custom elements default to `display: inline` with `min-width: auto`.
      // Inside a flex/grid parent (e.g. the demo site's
      // `.ppp-section-column-content-stack`, which is `display: flex`), that
      // means the host element's flex `min-width` resolves to its CONTENT's
      // min-content size — including the intrinsic width of any wide tables
      // inside the React tree — and the host blows past its parent's
      // bounds. `display: block; min-width: 0; max-width: 100%` is the
      // matching pair to `.ppp-section`'s own defensive sizing (see
      // Section.razor.css): block-level so percentage children resolve
      // sanely, `min-width: 0` so the flex algorithm doesn't widen us to
      // content size, and `max-width: 100%` belt-and-braces in case any
      // ancestor sets `width` rather than relying on the flex stretch.
      this.style.display = 'block';
      this.style.minWidth = '0';
      this.style.maxWidth = '100%';

      // A tick later, after any element this one replaced has been taken out: a Blazor page that
      // was prerendered swaps its elements for new ones as it becomes interactive. The sample the
      // old element held moves into this one rather than starting again, which would fetch its
      // data a second time.
      this.pendingMount = window.setTimeout(() => {
        this.pendingMount = null;
        const parked = takeParked(tagName);
        if (parked) {
          this.appendChild(parked.container);
          this.mounted = parked;
        } else {
          this.mount();
        }
      }, 0);
    }

    private mount(): void {
      const container = document.createElement('div');
      this.appendChild(container);
      const root = createRoot(container);
      this.mounted = { root, container };
      let content: ReactNode = <Component />;
      if (config?.recordContext) {
        // Spread `id` only when provided — passing `id={undefined}`
        // explicitly to RecordContext is the create-mode signal, but the
        // type accepts `string | undefined` and the framework treats
        // omitted-vs-undefined the same. Keeping the spread tidy.
        //
        // When `skipFetch` is set (and `id` is present), seed the
        // RecordContext with a stub `loadedRecord` so the server fetch is
        // bypassed and descendants see `record` immediately on first
        // render. Mirrors the Blazor demo pattern for SubGrid-style
        // samples where the parent record is only used as a relationship
        // filter key, not for its column values.
        const stubLoaded: TableRecord | undefined =
          config.recordContext.skipFetch && config.recordContext.id !== undefined
            ? {
                tableName: config.recordContext.table,
                id: config.recordContext.id,
                properties: {},
              }
            : undefined;
        content = (
          <RecordContext
            table={config.recordContext.table}
            {...(config.recordContext.id !== undefined && { id: config.recordContext.id })}
            {...(stubLoaded !== undefined && { loadedRecord: stubLoaded })}
          >
            {content}
          </RecordContext>
        );
      }
      // Gate render on the framework's default localization bundle so
      // samples using `t(...)` paint with translated strings on first
      // commit instead of flashing raw keys for the ~50-100ms it takes
      // the bundle to resolve. The boundary's default fallback is a
      // centered Fluent Spinner; on a cache-hot navigation the bundle
      // is already settled and the boundary mounts children immediately
      // with no spinner shown.
      //
      // `storageKey="ppp-sample-theme"` scopes the theme provider's
      // localStorage to a sample-only slot. `<BlazorThemeSync>` writes
      // into the provider via `setMode` / `setAccentColor` on every
      // Blazor host theme change; without the dedicated key those
      // writes would clobber the user's real React-app theme preference
      // (`ppp-theme-mode` / `ppp-theme-accent`).
      //
      // The full FluentUI Blazor `OfficeColor` palette ships as built-in
      // accents on `<PowerPortalsProThemeProvider>`, so `BlazorThemeSync`
      // can `setAccentColor(name)` for any Office brand the host applies
      // without this envelope registering `customAccents`.
      root.render(
        <PowerPortalsProThemeProvider
          storageKey="ppp-sample-theme"
          // Let the Blazor host's surface show through rather than
          // painting the FluentProvider's own `colorNeutralBackground1`
          // over the Section the sample sits in, so the React/Blazor seam
          // stays invisible. Docs envelope only: consumer apps that mount
          // `<PowerPortalsProThemeProvider>` directly keep its surface.
          style={{ backgroundColor: 'transparent' }}
        >
          <BlazorThemeSync />
          <PowerPortalsProProvider>
            <LocalizationBoundary>
              <FluentDialogProvider>
                <FluentOverlayProvider>
                  {/* AddressDisplayValue's map pin opens its map through this. */}
                  <MapsProvider>{content}</MapsProvider>
                </FluentOverlayProvider>
              </FluentDialogProvider>
            </LocalizationBoundary>
          </PowerPortalsProProvider>
        </PowerPortalsProThemeProvider>,
      );
    }

    disconnectedCallback(): void {
      if (this.pendingMount !== null) {
        window.clearTimeout(this.pendingMount);
        this.pendingMount = null;
      }
      // Kept for a replacement to take over, and unmounted if none does.
      if (this.mounted) park(tagName, this.mounted);
      this.mounted = null;
    }
  }

  customElements.define(tagName, SampleElement);
}

/** A sample's React root, and the element it renders into inside the sample's own element. */
interface MountedSample {
  root: Root;
  container: HTMLElement;
}

// The samples whose elements were taken out of the page, by tag, for the element that replaces
// one to take over. A replacement comes in the same DOM update as the removal, so a sample nobody
// has taken a moment later is unmounted; and only on the same page, query and all, so one shown
// again after a navigation starts afresh rather than with the state it was left in.
const parkedSamples = new Map<string, { sample: MountedSample; page: string; timer: number }[]>();

const currentPage = (): string => window.location.pathname + window.location.search;

function unpark(tagName: string, index: number) {
  const list = parkedSamples.get(tagName)!;
  const [entry] = list.splice(index, 1);
  if (list.length === 0) parkedSamples.delete(tagName);
  return entry!;
}

function park(tagName: string, sample: MountedSample): void {
  const entry = {
    sample,
    page: currentPage(),
    timer: window.setTimeout(() => {
      const index = parkedSamples.get(tagName)?.indexOf(entry) ?? -1;
      if (index >= 0) unpark(tagName, index);
      sample.root.unmount();
    }, 50),
  };
  parkedSamples.set(tagName, [...(parkedSamples.get(tagName) ?? []), entry]);
}

function takeParked(tagName: string): MountedSample | null {
  const page = currentPage();
  const index = parkedSamples.get(tagName)?.findIndex((x) => x.page === page) ?? -1;
  if (index < 0) return null;
  const entry = unpark(tagName, index);
  window.clearTimeout(entry.timer);
  return entry.sample;
}
